<template>
  <tab-list
      v-model="currentTab"
      :tabs-enum="tabs"
      :title="title"
      :loading="isLoading"
      :default-tab="defaultTab"
      @change="changeTab">
    <template v-slot:tabItem>
      <general-error v-if="!!generalError" />
      <v-progress-linear
          :active="isLoading"
          color="loader"
          indeterminate />
      <v-row v-if="showGeneralInfo" class="text-right">
        <v-col class="text-right">
          <btn-icon-tip
              id="sendNotification"
              icon="notification_important"
              icon-color="primary"
              :tip="$t('Отправить повторное уведомление о регистрации')"
              @click="showNotificationDialog" />
          <notification-of-organization-acceptance-dialog
              v-if="notificationDialog.show"
              :dialog-prop="notificationDialog"
              @close="closeDialog('notificationDialog')" />
          <btn-icon-tip
              v-if="showGeneralInfo"
              :id="`getRegCard-${organizationId}`"
              icon="description"
              :tip="$t('Скачать регистрационную карточку')"
              @click="getRegistrationCard" />
          <!-- </v-col>
        <v-col class="text-right"> -->
          <btn-icon-tip
              v-if="isAdmin && showGeneralInfo"
              :id="`sendToCRM-${organizationId}`"
              b-class="mx-3"
              icon="outgoing_mail"
              :tip="$t('Отправить запрос в CRM')"
              @click="openConfirmDialog" />
          <confirm-dialog
              v-if="confirmDialog.show"
              :dialog-prop="confirmDialog"
              @close="closeDialog('confirmDialog')" />
        </v-col>
      </v-row>
      <organization-info-view
          v-if="showGeneralInfo"
          :organization-info="organization" />
      <organization-users
          v-if="showUsers"
          :can-add-users="canAddUsers"
          :organization-id="organizationId" />
      <identity-document-list-view
          v-if="showDocuments"
          :request-files="requestFiles"
          :document-types="requiredDocumentTypes"
          :loading="isLoading" />
    </template>
  </tab-list>
</template>

<script>
  import BtnIconTip from '@/components/reused/ui/BtnIconTip.vue'
  import OrganizationInfoView from '@/components/reused/organization/OrganizationInfoView.vue'
  import ConfirmDialog from '@/components/reused/common/ConfirmDialog.vue'
  import IdentityDocumentListView from '@/components/reused/files/IdentityDocumentListView.vue'
  import OrganizationUsers from './OrganizationUsers.vue'
  import TabList from '@/components/reused/ui/TabList.vue'
  import GeneralError from '@/components/reused/common/GeneralError.vue'
  import NotificationOfOrganizationAcceptanceDialog from './NotificationOfOrganizationAcceptanceDialog.vue'

  import organizationMixin from '@/utils/mixins/scoped/organizationMixin.js'

  import { ORGANIZATION_TABS_ADMIN_ENUM } from '@/constants/enums'
  import {
    ORGANIZATION_TABS,
    PARTICIPANT_TYPES
  } from '@/constants/business'
  import { mapActions, mapState } from 'vuex'
  import { ORGANIZATION_MANAGEMENT, IDENTITY_FILES } from '@/constants/store.js'

  export default {
    components: {
      TabList,
      BtnIconTip,
      OrganizationInfoView,
      IdentityDocumentListView,
      OrganizationUsers,
      GeneralError,
      ConfirmDialog,
      NotificationOfOrganizationAcceptanceDialog
    },
    mixins: [
      organizationMixin
    ],
    props: {
      organizationId: {
        type: String,
        required: true
      },
      tab: {
        type: String
      }
    },
    data () {
      return {
        isLoading: false,
        currentTab: this.tab || ORGANIZATION_TABS.GENERAL_INFO,
        defaultTab: ORGANIZATION_TABS.GENERAL_INFO,
        notificationDialog: {
          show: false,
          organizationId: null
        },
        confirmDialog: {
          show: false,
          additional: null,
          text: null,
          callback: null,
          warning: true
        }
      }
    },
    computed: {
      ...mapState(ORGANIZATION_MANAGEMENT.NAMESPACE, {
        organization: state => (state[ORGANIZATION_MANAGEMENT.STATE.CURRENT_ORGANIZATION] || { })
      }),
      ...mapState(IDENTITY_FILES.NAMESPACE, {
        requiredDocumentTypes: state => (state[IDENTITY_FILES.STATE.REQUIRED_DOCUMENT_TYPES] || [])
      }),
      requestFiles () {
        return this.organization.files || []
      },
      tabs () {
        const tabs = { ...ORGANIZATION_TABS_ADMIN_ENUM }

        return tabs
      },
      canAddUsers () {
        return this.isAdmin &&
          (this.organization.participantType === PARTICIPANT_TYPES.ORGANIZER ||
            this.organization.participantType === PARTICIPANT_TYPES.SUPER_ORGANIZER)
      },
      requestType () {
        return this.getRequestTypeByOrganization(
          this.organization.participantType)
      },
      showGeneralInfo () {
        return this.currentTab === ORGANIZATION_TABS.GENERAL_INFO
      },
      showUsers () {
        return this.currentTab === ORGANIZATION_TABS.USERS
      },
      showDocuments () {
        return this.currentTab === ORGANIZATION_TABS.DOCUMENTS
      },
      title () {
        return `${this.$t('Просмотр организации')} ${this.organization.organizationShortName}`
      }
    },
    created () {
      const vm = this
      vm.clearStore().then(() => {
        if (!vm.organizationId) {
          return
        }
        vm.initialize()
      })
    },
    beforeDestroy () {
      this.clearStore()
    },
    methods: {
      ...mapActions(ORGANIZATION_MANAGEMENT.NAMESPACE, {
        sendToCRMAction: ORGANIZATION_MANAGEMENT.ACTIONS.SEND_TO_CRM,
        getOrganizationAdminAction: ORGANIZATION_MANAGEMENT.ACTIONS.GET_ORGANIZATION_ADMIN,
        getRegistrationCardAction: ORGANIZATION_MANAGEMENT.ACTIONS.GET_REGISTRATION_CARD,
        clearOrganizationAdminAction: ORGANIZATION_MANAGEMENT.ACTIONS.CLEAR_ORGANIZATION_ADMIN
      }),
      ...mapActions(IDENTITY_FILES.NAMESPACE, {
        getRequiredDocumentTypesAction: IDENTITY_FILES.ACTIONS.GET_REQUIRED_DOCUMENT_TYPES,
        clearRequiredDocumentTypesAction: IDENTITY_FILES.ACTIONS.CLEAR_REQUIRED_DOCUMENT_TYPES
      }),
      changeTab: function (tabValue) {
        this.replaceCurrentRoute(this.getRoute(tabValue))
      },
      getRoute: function (tabValue) {
        return `${this.urlRootPath}/organization/${this.organizationId}/${tabValue}`
      },
      clearStore: async function () {
        await Promise.all(
          [
            this.clearOrganizationAdminAction(),
            this.clearRequiredDocumentTypesAction()
          ])
      },
      initialize: async function () {
        this.isLoading = true
        try {
          await this.getOrganizationAdminAction({
            organizationId: this.organizationId
          })
          await this.getRequiredDocumentTypesAction({
            requestType: this.requestType
          })
          this.isLoading = false
        } catch (error) {
          this.isLoading = false
          this.throwIfNotServerError(error)
        }
      },
      getRegistrationCard: async function () {
        try {
          this.isLoading = true
          await this.getRegistrationCardAction({
            organizationId: this.organizationId
          })
        } catch (error) {
          this.throwIfNotServerError(error)
        } finally {
          this.isLoading = false
        }
      },
      sendToCRM: async function () {
        try {
          this.isLoading = true
          await this.sendToCRMAction({
            organizationId: this.organizationId
          })
        } catch (error) {
          this.throwIfNotServerError(error)
        } finally {
          this.isLoading = false
        }
      },
      showNotificationDialog: function () {
        this.notificationDialog.organizationId = this.organizationId
        this.notificationDialog.show = true
      },
      openConfirmDialog: function () {
        this.confirmDialog.text = this.$t('Вы действительно хотите отправить новый запрос для обработки в Смумрике?')
        this.confirmDialog.callback = this.sendToCRM
        this.confirmDialog.warning = true
        this.confirmDialog.show = true
      }
    }
  }
</script>
